import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useMemo } from 'react';
import { RiBatteryLowLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FieldNumber } from '@/components/ui/kit/Field';
import Select from '@/components/ui/kit/Select';
import LabelGroup from '@/components/ui/LabelGroup';
import ProgressBar from '@/components/ui/ProgressBar';
import useParamCommand from '@/hooks/command/mavlink/useParamCommand';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeMavLinkCommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: [robot] }) => {
  const dispatch = useDispatch();
  const robotParams = useSelector((state) => state.robotParams[robot.id]);
  const [params, setParams] = useState({
    BATT_FS_LOW_ACT: null,
    BATT_LOW_VOLT: null,
    BATT_LOW_MAH: null,
    BATT_LOW_TIMER: null,
  });
  const [isProcessing, setProcessing] = useState(false);

  const { getAddress } = useMavLinkAddress();
  const paramCommand = useParamCommand();

  // 수신된 파라미터 갯수
  const isLoadedCount = useMemo(() => {
    return Object.values(params).filter((value) => value !== null).length;
  }, [params]);

  // 파라미터 수신 중 여부
  const isLoading = useMemo(() => {
    return isLoadedCount < Object.keys(params).length;
  }, [isLoadedCount]);

  // Failsafe Action 활성화 여부
  const isEnabled = useMemo(() => {
    return params['BATT_FS_LOW_ACT']?.value !== 0;
  }, [params['BATT_FS_LOW_ACT']?.value]);

  useMountEffect(() => {
    // 이미 저장된 파라미터가 있으면 먼저 적용
    setParams((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(prev)) {
        const stored = robotParams.params[key];
        if (stored) {
          next[key] = { value: stored.value, type: stored.type };
        }
      }
      return next;
    });

    const address = getAddress(robot.id);
    const paramsGroup = Object.keys(params).map((key) => [...address, key, -1]);

    paramCommand.read(robot, paramsGroup);
  });

  useUpdateEffect(() => {
    const { lastParamId } = robotParams;
    if (!Object.keys(params).includes(lastParamId)) return;

    setParams((prev) => ({
      ...prev,
      [lastParamId]: {
        value: robotParams.params[lastParamId].value,
        type: robotParams.params[lastParamId].type,
      },
    }));
  }, [robotParams]);

  const handleChange = (e) => {
    const nextParams = { ...params };
    nextParams[e.target.name].value = Number(e.target.value);

    setParams(nextParams);
  };

  //#region Process Functions
  const process = async () => {
    try {
      const paramSet = Object.entries(params)
        .filter(([key]) => (isEnabled ? true : key === 'BATT_FS_LOW_ACT'))
        .map(([id, { value, type }]) => ({ id, value, type }));

      for (const param of paramSet) {
        await executeMavLinkCommand(robot, paramCommand.set(param.id, param.value, param.type));
      }

      onSuccess(robot);
    } catch (error) {
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  const onSuccess = (robot) => {
    notifier.success(`Failsafe settings saved successfully on ${robot.name}.`);
    setProcessing(false);
  };

  const onFailure = (robot) => {
    notifier.error(`Failed to save failsafe settings on ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    process();
  }, [isProcessing]);

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Configure battery failsafe settings</div>
      {isLoading && <ProgressBar current={isLoadedCount} total={Object.keys(params).length} />}
      {!isLoading && (
        <>
          <div className={cx('form')}>
            <LabelGroup htmlFor="BATT_FS_LOW_ACT" label="Low Action">
              <Select
                id="BATT_FS_LOW_ACT"
                name="BATT_FS_LOW_ACT"
                value={params['BATT_FS_LOW_ACT'].value}
                onChange={handleChange}>
                <option value={0}>Disabled</option>
                <option value={robot.model.vehicle === 'Copter' ? 1 : 2}>Land</option>
                <option value={robot.model.vehicle === 'Copter' ? 2 : 1}>RTL</option>
              </Select>
            </LabelGroup>
            {isEnabled && (
              <>
                <LabelGroup htmlFor="BATT_LOW_VOLT" label="Low Voltage Threshold">
                  <div className={cx('fieldWrapper')}>
                    <FieldNumber
                      id="BATT_LOW_VOLT"
                      name="BATT_LOW_VOLT"
                      value={params['BATT_LOW_VOLT'].value}
                      onChange={handleChange}
                    />
                    <span className={cx('unit')}>V</span>
                  </div>
                </LabelGroup>
                <LabelGroup htmlFor="BATT_LOW_MAH" label="Low mAh Threshold">
                  <div className={cx('fieldWrapper')}>
                    <FieldNumber
                      id="BATT_LOW_MAH"
                      name="BATT_LOW_MAH"
                      value={params['BATT_LOW_MAH'].value}
                      onChange={handleChange}
                    />
                    <span className={cx('unit')}>mAh</span>
                  </div>
                </LabelGroup>
                <LabelGroup htmlFor="BATT_LOW_TIMER" label="Low Timer">
                  <div className={cx('fieldWrapper')}>
                    <FieldNumber
                      id="BATT_LOW_TIMER"
                      name="BATT_LOW_TIMER"
                      value={params['BATT_LOW_TIMER'].value}
                      onChange={handleChange}
                    />
                    <span className={cx('unit')}>sec</span>
                  </div>
                </LabelGroup>
              </>
            )}
          </div>
          <ConfirmSlider
            width="100%"
            height={40}
            onConfirm={doCommand}
            isProcessing={isProcessing}
            labelStyle={{ fontSize: 12 }}
          />
        </>
      )}
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Battery';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiBatteryLowLine;
Command.Panel = Panel;

export default Command;
